/**
 * Feature Request API — CRUD for feature requests with voting.
 */

import { supabase } from '@turnifly/shared/api'

export interface FeatureRequestItem {
  id: string
  user_id: string
  title: string
  description: string
  votes_count: number
  status: 'open' | 'in_progress' | 'done' | 'closed'
  created_at: string
  is_voted_by_me?: boolean
}

export async function fetchFeatureRequests(
  userId: string,
  sortBy: 'votes' | 'newest' = 'votes',
): Promise<FeatureRequestItem[]> {
  const order = sortBy === 'votes'
    ? { column: 'votes_count', ascending: false }
    : { column: 'created_at', ascending: false }

  const { data: requests } = await supabase
    .from('feature_requests')
    .select('*')
    .order(order.column, { ascending: order.ascending })
    .limit(50)
    .throwOnError()

  if (!requests?.length) return []

  const requestIds = requests.map((r) => (r as { id: string }).id)
  const { data: votes } = await supabase
    .from('feature_votes')
    .select('feature_id')
    .eq('user_id', userId)
    .in('feature_id', requestIds)
    .throwOnError()

  const votedSet = new Set((votes ?? []).map((v) => (v as { feature_id: string }).feature_id))

  return (requests as FeatureRequestItem[]).map((r) => ({
    ...r,
    is_voted_by_me: votedSet.has(r.id),
  }))
}

export async function createFeatureRequest(
  userId: string,
  title: string,
  description: string,
): Promise<unknown> {
  const { data } = await supabase
    .from('feature_requests')
    .insert({ user_id: userId, title, description })
    .select()
    .single()
    .throwOnError()
  return data
}

export async function voteFeatureRequest(
  userId: string,
  featureId: string,
): Promise<void> {
  await supabase
    .from('feature_votes')
    .insert({ user_id: userId, feature_id: featureId })
    .throwOnError()
}

export async function unvoteFeatureRequest(
  userId: string,
  featureId: string,
): Promise<void> {
  await supabase
    .from('feature_votes')
    .delete()
    .eq('user_id', userId)
    .eq('feature_id', featureId)
    .throwOnError()
}
